import bookingAssistantAPI from '../services/bookingAssistantAPI';

const LOCATION_ALIASES = {
  kadaba: 'Kadaba',
  mangalore: 'Mangaluru',
  mangaluru: 'Mangaluru',
  manglore: 'Mangaluru',
  bangalore: 'Bengaluru',
  bengaluru: 'Bengaluru',
  mysore: 'Mysuru',
  mysuru: 'Mysuru',
  udupi: 'Udupi',
  hubli: 'Hubli',
  goa: 'Goa',
  mumbai: 'Mumbai',
  delhi: 'Delhi',
};

const KANNADA_LOCATIONS = [
  [/ಕಡಬ/u, 'Kadaba'],
  [/ಮಂಗಳೂರು|ಮಂಗಲೂರು/u, 'Mangaluru'],
  [/ಬೆಂಗಳೂರು/u, 'Bengaluru'],
  [/ಮೈಸೂರು/u, 'Mysuru'],
  [/ಉಡುಪಿ/u, 'Udupi'],
];

const HINDI_LOCATIONS = [
  [/कडबा/u, 'Kadaba'],
  [/मैंगलोर|मंगलौर/u, 'Mangaluru'],
  [/बेंगलुरु|बैंगलोर/u, 'Bengaluru'],
  [/मैसूर/u, 'Mysuru'],
  [/उडुपी/u, 'Udupi'],
  [/गोवा/u, 'Goa'],
  [/मुंबई/u, 'Mumbai'],
  [/दिल्ली/u, 'Delhi'],
];

const MEETING_RE =
  /meeting\s*room|conference|board\s*room|ಮೀಟಿಂಗ್|मीटिंग|सम्मेलन|seminar|workshop/i;
const STAY_RE =
  /hotel|stay|resort|homestay|villa|pg|guest\s*house|ಹೋಟೆಲ್|ತಂಗ|होटल|रात|कमरा|स्टे/i;
const BOOK_RE = /book|ಬುಕ್|ಬುಕ್\s*ಮಾಡಿ|बुक|बुकिंग|reserve|confirm/i;
const SEARCH_RE =
  /search|find|show|list|ಹುಡುಕ|ಶೋ\s*ಮಾಡಿ|ತೋರಿಸ|खोज|ढूंढ|दिखाओ|दिखाइए/i;
const FILTER_RE = /under|below|cheap|budget|filter|₹|less\s*than|ಕಡಿಮೆ|कम|सस्ता/i;

const extractLocalDestination = (text) => {
  const raw = String(text || '');

  for (const [pattern, value] of [...KANNADA_LOCATIONS, ...HINDI_LOCATIONS]) {
    if (pattern.test(raw)) return value;
  }

  const lower = raw.toLowerCase();

  const glued = lower.match(/\b([a-z]{3,})(dalli|alli|nalli|mein|men|main)\b/i);
  if (glued) {
    const key = glued[1].toLowerCase();
    return LOCATION_ALIASES[key] || key.charAt(0).toUpperCase() + key.slice(1);
  }

  const inMatch = lower.match(/\b(?:in|at|near|for|alli|dalli|nalli|mein|men)\s+([a-z\u0900-\u097F\u0C80-\u0CFF]{2,})/i);
  if (inMatch) {
    const key = inMatch[1].toLowerCase();
    if (LOCATION_ALIASES[key]) return LOCATION_ALIASES[key];
    return inMatch[1].charAt(0).toUpperCase() + inMatch[1].slice(1);
  }

  for (const [key, value] of Object.entries(LOCATION_ALIASES)) {
    if (lower.includes(key)) return value;
  }

  const words = raw.trim().split(/\s+/);
  if (words.length === 1 && words[0].length >= 3) {
    const key = words[0].toLowerCase();
    return LOCATION_ALIASES[key] || words[0].charAt(0).toUpperCase() + words[0].slice(1);
  }

  return null;
};

const extractParticipants = (text) => {
  const lower = String(text || '').toLowerCase();
  const patterns = [
    /(\d+)\s*(?:people|persons|participants|attendees|janar|janag|janarige|janagige|लोग|व्यक्ति)/i,
    /for\s+(\d+)/i,
    /(\d+)\s*(?:janarige|janagige)/i,
  ];
  for (const re of patterns) {
    const m = lower.match(re);
    if (m) return Math.min(500, Math.max(1, parseInt(m[1], 10)));
  }
  return null;
};

const extractBudget = (text) => {
  const m = String(text || '').match(/(?:under|below|less\s*than|₹|rs\.?|rupees?)\s*(\d[\d,]*)/i);
  if (m) return parseInt(m[1].replace(/,/g, ''), 10);
  const m2 = String(text || '').match(/(\d[\d,]*)\s*(?:rupees?|rs|₹)/i);
  if (m2) return parseInt(m2[1].replace(/,/g, ''), 10);
  return null;
};

const isComplexCommand = (text) => {
  const t = String(text || '');
  if (MEETING_RE.test(t) || STAY_RE.test(t) || BOOK_RE.test(t) || SEARCH_RE.test(t) || FILTER_RE.test(t)) {
    return true;
  }
  return t.trim().split(/\s+/).length > 4;
};

export const parseLocalVoiceCommand = (transcript) => {
  const text = String(transcript || '').trim();
  if (!text) return null;

  const destination = extractLocalDestination(text);
  const participants = extractParticipants(text);
  const maxBudget = extractBudget(text);
  const isMeeting = MEETING_RE.test(text);
  const isStay = STAY_RE.test(text) && !isMeeting;
  const wantsBook = BOOK_RE.test(text);
  const wantsSearch = SEARCH_RE.test(text) || !wantsBook;

  return {
    transcript: text,
    destination,
    bookingType: isMeeting ? 'meeting' : isStay ? 'stay' : null,
    participants,
    maxBudget,
    action: wantsBook ? 'book' : wantsSearch ? 'search' : 'search',
    needsAi: isComplexCommand(text),
  };
};

export const parseVoiceCommand = async (transcript, language = 'en') => {
  const local = parseLocalVoiceCommand(transcript);
  if (!local) return null;

  if (!local.needsAi) {
    return { source: 'local', ...local };
  }

  try {
    const res = await bookingAssistantAPI.chat({
      message: transcript,
      history: [],
      language,
      guestKey: bookingAssistantAPI.guestKey(),
    });

    const intent = res.data.intent || {};
    return {
      source: 'ai',
      transcript,
      destination: intent.destination || intent.location || local.destination,
      bookingType: intent.bookingType || local.bookingType,
      participants: intent.guests?.adults || intent.capacity || local.participants,
      maxBudget: intent.maxBudget || local.maxBudget,
      checkIn: intent.checkIn,
      checkOut: intent.checkOut,
      guests: intent.guests,
      action: local.action,
      intent,
      reply: res.data.reply,
      recommendations: res.data.recommendations || [],
      sessionId: res.data.sessionId,
    };
  } catch {
    return { source: 'local', ...local };
  }
};

export const intentToSearchState = (parsed) => ({
  location: parsed?.destination || parsed?.intent?.destination || '',
  propertyType: (parsed?.bookingType || parsed?.intent?.bookingType) === 'meeting' ? 'meeting room' : 'all',
  guests: parsed?.guests || (parsed?.participants ? { adults: parsed.participants } : null),
  checkIn: parsed?.checkIn || parsed?.intent?.checkIn || null,
  checkOut: parsed?.checkOut || parsed?.intent?.checkOut || null,
  maxBudget: parsed?.maxBudget || parsed?.intent?.maxBudget || null,
});

export default parseVoiceCommand;
